import React from "react";
import { ConstraintV2 } from "../../../types/adapter";
import { DefaultColumnBuilder as builder } from "../../components/ColumnBuilder";
import { Button, Popconfirm, Space, Table, Tag, Tooltip } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

export interface ConstraintV2TableProps {
  loading?: boolean;
  records: ConstraintV2[];
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

const MANUAL_SOURCE = "manual";

const sourceTag = (source: string) => (
  <Tag color={source === MANUAL_SOURCE ? "blue" : "default"}>{source}</Tag>
);

/**
 * constraints_v2 is authoritative post-cutover (SDK-679) — this is now the sole constraints
 * table for an adapter. Rows are keyed by canonical `dependencyKey` and carry a `source`
 * provenance: "manual" rows were added here and can be deleted; "crawler"-derived rows are
 * read-only (deleting them would just have the crawler recreate them on the next run).
 * **Corrected 2026-09-18** (`fix/constraints-v2-source-key`, unique key gains `source`): the
 * sentence above is wrong — rows are keyed by DB `id`, not by canonical `dependencyKey`. Once
 * `source` joins the unique key, the same `(dependencyKey, operator)` pair can appear more than
 * once across sources (e.g. a "manual" row beside a "cocoapods" one). The table already keys by
 * `record.id` (`dataSource` below) and deletes by `record.id` (`onDelete(record.id)`), and the
 * manual-only delete gating (`deletable = record.source === MANUAL_SOURCE`) is unaffected by the
 * quad-key change — both were already correct. Only display strings that embedded
 * `dependencyKey` alone (aria-label, Popconfirm description) were ambiguous and have been fixed
 * to include `source` and `operator` too.
 *
 * **Corrected 2026-09-22** (adapter full-CRUD task, "curated/lock switch" explicitly rejected):
 * the "crawler-derived rows are read-only" half above is wrong and the manual-only delete gating
 * it describes has been removed. Every row, regardless of `source`, now gets edit + delete —
 * `PUT`/`DELETE /v4/adapter/{id}/constraints/v2/{constraintId}` both work for any source. A
 * non-manual row's actions carry a non-blocking informational tooltip instead: editing/deleting
 * it is allowed, but the next crawl of this adapter version may overwrite the change.
 */
export const ConstraintV2Table = (props: ConstraintV2TableProps) => {
  const columns = [
    builder.id,
    { ...builder.text("Dependency Key", "dependencyKey"), width: 200 },
    builder.text("Version", "version"),
    builder.text("Operator", "operator"),
    {
      title: "Source",
      dataIndex: "source",
      render: (text: string) => sourceTag(text),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      fixed: "right" as const,
      render: (_: unknown, record: AnyObject) => {
        const crawlerOwned = record.source !== MANUAL_SOURCE;

        const actions = (
          <Space size="small">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label={`Edit constraint ${record.dependencyKey} ${record.operator} (${record.source})`}
              onClick={() => props.onEdit(record.id)}
            />
            <Popconfirm
              title="Delete constraint"
              description={`Remove the ${record.source} constraint on "${record.dependencyKey}" (${record.operator} ${record.version})?`}
              okText="Yes"
              cancelText="No"
              onConfirm={() => props.onDelete(record.id)}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Delete constraint ${record.dependencyKey} ${record.operator} (${record.source})`}
              />
            </Popconfirm>
          </Space>
        );

        if (!crawlerOwned) {
          return actions;
        }

        return (
          <Tooltip title="Crawler-owned row: the next crawl of this adapter version may overwrite manual changes">
            {actions}
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Table
      loading={props.loading}
      columns={columns}
      pagination={false}
      scroll={{ x: "max-content" }}
      locale={{ emptyText: "No constraints" }}
      dataSource={(props.records || []).map((record) => ({
        ...record,
        key: record.id,
      }))}
    />
  );
};
